import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/store';
import { selectConstant } from '@/store/slices/constantsSlice';
import type { StreamRef } from '@/types/streaming';
import type { WatchEntry } from '@/services/historyApi';

interface Props {
  videoRef: RefObject<HTMLVideoElement>;
  streamRef: StreamRef;
  entries: WatchEntry[];
  /**
   * Called when the prompt resolves (Resume or Start over). Watch.tsx uses
   * this to re-enable history recording, which it suppresses while the modal
   * is open so the throttled timeupdate writes don't trample the user's
   * resume choice.
   */
  onResolved?: () => void;
}

const COUNTDOWN_MS = 10_000;

function findMatchingEntry(streamRef: StreamRef, entries: WatchEntry[]): WatchEntry | null {
  if (streamRef.type === 'movie') {
    return entries.find((e) => e.movieId === streamRef.tmdbId) ?? null;
  }
  if (streamRef.type === 'anime') {
    return entries.find((e) =>
      e.animeMalId === streamRef.malId
      && e.episodeNumber === streamRef.episode
      && e.animeAudio === streamRef.audio,
    ) ?? null;
  }
  return entries.find((e) =>
    e.tvId === streamRef.tmdbId
    && e.seasonNumber === streamRef.season
    && e.episodeNumber === streamRef.episode,
  ) ?? null;
}

function formatMmSs(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

export function ResumePrompt({ videoRef, streamRef, entries, onResolved }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const minResumableProgressMs = useAppSelector((s) => selectConstant(s, 'resumeMinProgressMs'));
  const nearCompleteThreshold = useAppSelector((s) => selectConstant(s, 'nearCompleteThreshold'));
  const nearCompleteFraction = nearCompleteThreshold / 100;

  // The entry is matched at mount and frozen — if the user resumes / dismisses,
  // we never re-show the prompt on subsequent timeupdate writes.
  const [match] = useState<WatchEntry | null>(() => findMatchingEntry(streamRef, entries));
  const [dismissed, setDismissed] = useState(false);
  const [remainingMs, setRemainingMs] = useState(COUNTDOWN_MS);

  const eligible = match !== null
    && match.progress >= minResumableProgressMs;

  // Inform the parent immediately when no prompt will render — otherwise the
  // parent's recording-suppression latch stays closed forever.
  useEffect(() => {
    if (!eligible && onResolved) onResolved();
    // run-on-mount only; eligibility is computed from the frozen match.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once mounted with a match, pause the video so the user actually sees the
  // modal rather than missing the prompt while playback drives past it.
  useEffect(() => {
    if (!eligible || dismissed) return;
    const video = videoRef.current;
    if (!video) return;
    try { video.pause(); } catch { /* ignore */ }
  }, [eligible, dismissed, videoRef]);

  // Skip prompts where the saved progress already crossed the
  // "treat as finished" threshold (95% of the now-loaded duration).
  // Read duration lazily — `loadedmetadata` may not have fired yet at mount.
  useEffect(() => {
    if (!eligible || dismissed) return;
    const video = videoRef.current;
    if (!video) return;
    function maybeSkipForFinished(): void {
      if (!video) return;
      const dur = video.duration;
      if (!Number.isFinite(dur) || dur <= 0 || !match) return;
      if (match.progress / 1000 / dur > nearCompleteFraction) {
        // Treat as finished — start fresh, no modal.
        setDismissed(true);
        try { void video.play(); } catch { /* ignore */ }
        onResolved?.();
      }
    }
    maybeSkipForFinished();
    video.addEventListener('loadedmetadata', maybeSkipForFinished);
    return (): void => { video.removeEventListener('loadedmetadata', maybeSkipForFinished); };
  }, [eligible, dismissed, match, videoRef, onResolved, nearCompleteFraction]);

  // 10s auto-resume countdown. Plain setInterval — the spec specifies real
  // time, not playback time (the video is paused while the modal is open).
  useEffect(() => {
    if (!eligible || dismissed) return;
    const start = Date.now();
    const handle = window.setInterval(() => {
      const left = Math.max(0, COUNTDOWN_MS - (Date.now() - start));
      setRemainingMs(left);
      if (left <= 0) {
        window.clearInterval(handle);
        resume();
      }
    }, 250);
    return (): void => { window.clearInterval(handle); };
    // resume is stable enough; React-hooks/exhaustive-deps would suggest
    // including it but that would create churn each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, dismissed]);

  function resume(): void {
    if (!match) return;
    const video = videoRef.current;
    if (video) {
      try {
        video.currentTime = match.progress / 1000;
        void video.play();
      } catch { /* ignore */ }
    }
    setDismissed(true);
    onResolved?.();
  }

  function startOver(): void {
    const video = videoRef.current;
    if (video) {
      try {
        video.currentTime = 0;
        void video.play();
      } catch { /* ignore */ }
    }
    setDismissed(true);
    onResolved?.();
  }

  if (!eligible || dismissed) return null;

  const remainingSec = Math.ceil(remainingMs / 1000);
  return (
    <div className="resume-prompt-overlay" role="dialog" aria-modal="true" aria-label={t('resume.dialogLabel')}>
      <div className="resume-prompt-card">
        <p className="resume-prompt-card__title">
          {t('resume.continueAt', { time: formatMmSs(match!.progress) })}
        </p>
        <div className="resume-prompt-card__actions">
          <button type="button" className="btn" onClick={resume} data-testid="resume-prompt-resume">
            {t('resume.resume')}
          </button>
          <button type="button" className="btn-ghost" onClick={startOver} data-testid="resume-prompt-restart">
            {t('resume.startOver')}
          </button>
        </div>
        <p className="resume-prompt-card__hint" role="status">
          {t('resume.autoResume', { seconds: remainingSec })}
        </p>
      </div>
    </div>
  );
}
